import React, { useEffect, useState, useMemo } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import logger from '../../utils/logger';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import moment from 'moment/moment';
import BreadCrumb from '../BreadCrumb';
import MoonLoader from 'react-spinners/MoonLoader';
import { toast } from 'react-toastify';


ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const pages = [
    { name: 'Reports', to: '/analytics', component: Link, current: false },
    { name: 'Job Card Status ', to: '/analytics/jobs', component: Link, current: true }, 
  
  ];
  

const JobStatusDashboard = () => {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setIsLoading(true);
    const fetchJobs = async () => {
      try {
        const querySnapshot = await getDocs(query(collection(db, "jobs")));
        const jobData = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();

          // Safely handle createdAt field
          let createdAt = "";
          if (data.createdAt && typeof data.createdAt.toDate === 'function') {
            createdAt = DateTime.fromJSDate(data.createdAt.toDate()).toISODate();
          }

          // Safely handle dateClosed field
          let dateClosed = "";
          if (data.status === "Closed" && data.tracker && data.tracker.length > 0) {
            const lastEntry = data.tracker[data.tracker.length - 1];
            if (lastEntry.Date && typeof lastEntry.Date.toDate === 'function') {
              dateClosed = DateTime.fromJSDate(lastEntry.Date.toDate()).toISODate();
            }
          }

          jobData.push({
            ...data,
            createdAt,
            dateClosed
          });
        });

        setJobs(jobData);
        setFilteredJobs(jobData);
      } catch (error) {
        logger.error('Error fetching jobs:', error);
        toast.error('Failed to load job data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobs();
  }, []);

 

  useEffect(() => {
    let sortedJobs = [...jobs].filter(job =>
      job.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.jobCardNo?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortConfig !== null) {
      sortedJobs.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    setFilteredJobs(sortedJobs);
  }, [searchTerm, jobs, sortConfig]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };



  const chartData = useMemo(() => ({
    labels: ['Open', 'Closed'],
    datasets: [
      {
        label: 'Job Status',
        data: [
          jobs.filter(job => job.status === "Open").length,
          jobs.filter(job => job.status === "Closed").length
        ],
        backgroundColor: ['rgba(255, 99, 132, 0.2)', 'rgba(54, 162, 235, 0.2)'],
        borderColor: ['rgba(255, 99, 132, 1)', 'rgba(54, 162, 235, 1)'],
        borderWidth: 1,
      },
    ],
  }), [jobs]);

  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);

  const paginate = pageNumber => setCurrentPage(pageNumber);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
        <BreadCrumb pages={pages} />
        <div className="text-center">
          <MoonLoader size={60} color="#6366F1" />
          <p className="mt-4 text-sm font-medium text-gray-600">Loading job data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-blue-50 py-4">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-br from-rose-50 to-pink-50 border-l-4 border-rose-500 rounded-xl p-4 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-rose-600 uppercase">Open Job Cards</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {jobs.filter(job => job.status === "Open").length}
                </p>
              </div>
              <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-l-4 border-emerald-500 rounded-xl p-4 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-emerald-600 uppercase">Closed Job Cards</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {jobs.filter(job => job.status === "Closed").length}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Job Cards Table */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4">
              <h3 className="text-xl font-bold text-white">Open & Closed Job Cards</h3>
              <p className="text-indigo-100 text-xs mt-1">All job cards with status tracking</p>
            </div>

            <div className="p-6">
              {/* Search Input */}
              <div className="mb-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by customer or job card number..."
                    className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  />
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        className="px-2 py-1.5 text-left text-xs font-black text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => requestSort('jobCardNo')}
                      >
                        Job Card No
                      </th>
                      <th
                        className="px-2 py-1.5 text-left text-xs font-black text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => requestSort('customerName')}
                      >
                        Customer
                      </th>
                      <th
                        className="px-2 py-1.5 text-left text-xs font-black text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => requestSort('createdAt')}
                      >
                        Date Created
                      </th>
                      <th
                        className="px-2 py-1.5 text-left text-xs font-black text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => requestSort('dateClosed')}
                      >
                        Date Closed
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {currentJobs.map((job, index) => (
                      <tr key={index} className="hover:bg-indigo-50 transition-colors duration-150">
                        <td className="px-2 py-1.5 text-xs">
                          <Link
                            to={`/vehicles/jobdetail/${job.jobCardNo}`}
                            className="text-indigo-600 hover:text-indigo-800 font-medium"
                          >
                            {job.jobCardNo}
                          </Link>
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">{job.customerName}</td>
                        <td className="px-2 py-1.5 text-xs text-gray-700">{moment(job.createdAt).format("DD-MMM-YYYY")}</td>
                        <td className="px-2 py-1.5 text-xs text-gray-700">
                          {moment(job.dateClosed).isValid() ? moment(job.dateClosed).format("DD-MMM-YYYY") :
                            <span className="text-gray-400 italic">Not Available</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-4 flex justify-center items-center gap-2">
                {Array.from({ length: Math.ceil(filteredJobs.length / jobsPerPage) }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => paginate(i + 1)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all duration-200 ${
                      currentPage === i + 1
                        ? 'bg-indigo-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4">
              <h3 className="text-xl font-bold text-white">Job Status Overview</h3>
              <p className="text-purple-100 text-xs mt-1">Visual breakdown of job statuses</p>
            </div>
            <div className="p-6">
              <Bar
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  plugins: {
                    legend: {
                      position: 'top',
                      labels: {
                        font: { size: 12 },
                        padding: 15
                      }
                    },
                    title: {
                      display: true,
                      text: 'Job Status Count',
                      font: { size: 14, weight: 'bold' },
                      padding: 20
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobStatusDashboard;